import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, X, ChevronDown, ChevronUp, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AutoProcessedAlert = ({ job, items, onDismiss, onDontShowAgain }) => {
  const [expanded, setExpanded] = useState(true);

  const total = items.reduce((sum, i) => sum + (parseFloat(i.slip_total_net) || 0), 0);
  const crews = [...new Set(items.map(i => i.crew_identifier))];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={{ duration: 0.2 }}
        className="absolute right-4 top-[4.5rem] z-50 w-[360px] rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
            <div>
              <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">
                Auto-Processed
              </p>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate max-w-[200px]">
                {job?.attachment_name || job?.email_subject || 'Invoice'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setExpanded(p => !p)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={onDismiss}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Breakdown */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              <div className="max-h-52 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-800/50">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 truncate">
                        {item.crew_identifier} · {item.description}
                      </p>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500">
                        {item.item_date}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <span className={`text-[11px] font-bold ${item.item_type === 'deduction' ? 'text-rose-500' : 'text-emerald-600'}`}>
                        {item.item_type === 'deduction' ? '−' : '+'}£{parseFloat(item.slip_total_net).toFixed(2)}
                      </span>
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary row */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
                <p className="text-[10px] text-slate-400 dark:text-slate-500">
                  {items.length} item{items.length !== 1 ? 's' : ''} · {crews.length} crew{crews.length !== 1 ? 's' : ''}
                </p>
                <p className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                  £{total.toFixed(2)} total
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer actions */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={onDontShowAgain}
            className="flex items-center gap-1.5 text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            <BellOff className="w-3 h-3" />
            Don't show again
          </button>
          <Button
            onClick={onDismiss}
            size="sm"
            variant="outline"
            className="h-6 px-3 text-[10px] font-semibold rounded-lg border-slate-200 dark:border-slate-700"
          >
            Dismiss
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AutoProcessedAlert;
